import React, { useState, useCallback } from 'react';
import { X, RotateCcw, Palette, Type, Check } from 'lucide-react';
import { Button } from './ui/button';

const FONT_OPTIONS = [
  'Inter, system-ui, sans-serif',
  'Montserrat, sans-serif',
  'Playfair Display, serif',
  'Roboto, sans-serif',
  'Poppins, sans-serif',
  'Lato, sans-serif',
  'Raleway, sans-serif',
  'Merriweather, serif',
];

const COLOR_FIELDS = [
  { key: 'primary', label: 'Primary' },
  { key: 'secondary', label: 'Secondary' },
  { key: 'accent', label: 'Accent' },
  { key: 'background', label: 'Background' },
  { key: 'text', label: 'Text' },
];

export default function ThemeCustomizer({
  theme,
  colorScheme,
  onColorSchemeChange,
  onFontChange,
  onApplyToAll,
  onClose,
}) {
  const [localScheme, setLocalScheme] = useState({ ...colorScheme });
  const [selectedFont, setSelectedFont] = useState(FONT_OPTIONS[0]);

  const handleColorChange = useCallback((key, value) => {
    const updated = { ...localScheme, [key]: value };

    // Auto-generate backgroundGradient when background changes
    if (key === 'background') {
      updated.backgroundGradient = `linear-gradient(135deg, ${value} 0%, ${darkenColor(value, 15)} 100%)`;
    }
    // Auto-derive textMuted and textSubtle from text color
    if (key === 'text') {
      updated.textMuted = hexToRgba(value, 0.7);
      updated.textSubtle = hexToRgba(value, 0.5);
    }

    setLocalScheme(updated);
    onColorSchemeChange(updated);
  }, [localScheme, onColorSchemeChange]);

  const handleFontChange = useCallback((font) => {
    setSelectedFont(font);
    if (onFontChange) onFontChange(font);
  }, [onFontChange]);

  const handleReset = useCallback(() => {
    if (theme?.colorScheme) {
      setLocalScheme({ ...theme.colorScheme });
      onColorSchemeChange({ ...theme.colorScheme });
    }
  }, [theme, onColorSchemeChange]);

  return (
    <div className="fixed right-0 top-0 h-full w-80 bg-white dark:bg-slate-900 shadow-2xl z-50 flex flex-col border-l border-slate-200 dark:border-slate-700 transition-transform duration-300">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-2">
          <Palette className="w-4 h-4 text-purple-500" />
          <span className="font-semibold text-sm text-slate-800 dark:text-white">Customize Theme</span>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Colors Section */}
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3">Colors</h3>
          <div className="space-y-3">
            {COLOR_FIELDS.map(({ key, label }) => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-sm text-slate-700 dark:text-slate-300">{label}</span>
                <div className="flex items-center gap-2">
                  <div
                    className="w-8 h-8 rounded-lg border-2 border-slate-200 dark:border-slate-600 cursor-pointer overflow-hidden relative"
                    style={{ backgroundColor: localScheme[key] || '#888' }}
                  >
                    <input
                      type="color"
                      value={toHex(localScheme[key]) || '#888888'}
                      onChange={(e) => handleColorChange(key, e.target.value)}
                      className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                    />
                  </div>
                  <span className="text-xs font-mono text-slate-400 w-16">{toHex(localScheme[key])}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Typography Section */}
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3">Typography</h3>
          <div className="space-y-1.5">
            {FONT_OPTIONS.map((font) => {
              const fontName = font.split(',')[0];
              const isSelected = selectedFont === font;
              return (
                <button
                  key={font}
                  onClick={() => handleFontChange(font)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                    isSelected
                      ? 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-700'
                      : 'hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300'
                  }`}
                >
                  <span style={{ fontFamily: font }}>{fontName}</span>
                  {isSelected && <Check className="w-3.5 h-3.5" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Preview Swatch */}
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3">Preview</h3>
          <div
            className="rounded-xl p-4 border border-slate-200 dark:border-slate-700"
            style={{ background: localScheme.backgroundGradient || localScheme.background }}
          >
            <div className="h-1 w-12 rounded mb-3" style={{ backgroundColor: localScheme.primary }} />
            <div className="text-sm font-bold mb-1" style={{ color: localScheme.text, fontFamily: selectedFont }}>
              Sample Title
            </div>
            <div className="text-xs" style={{ color: localScheme.textMuted || hexToRgba(localScheme.text, 0.7), fontFamily: selectedFont }}>
              Sample body text with muted color
            </div>
            <div className="flex gap-2 mt-3">
              <div className="w-6 h-6 rounded-full" style={{ backgroundColor: localScheme.primary }} />
              <div className="w-6 h-6 rounded-full" style={{ backgroundColor: localScheme.secondary }} />
              <div className="w-6 h-6 rounded-full" style={{ backgroundColor: localScheme.accent }} />
            </div>
          </div>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="p-4 border-t border-slate-200 dark:border-slate-700 space-y-2">
        {onApplyToAll && (
          <Button
            onClick={() => onApplyToAll(localScheme)}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white"
            size="sm"
          >
            Apply to All Slides
          </Button>
        )}
        <Button
          onClick={handleReset}
          variant="outline"
          className="w-full"
          size="sm"
        >
          <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
          Reset to Default
        </Button>
      </div>
    </div>
  );
}

// Helper: convert any color to hex
function toHex(color) {
  if (!color) return '#888888';
  if (color.startsWith('#')) return color.substring(0, 7);
  // Try to extract from rgba
  const rgbaMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (rgbaMatch) {
    const r = parseInt(rgbaMatch[1]).toString(16).padStart(2, '0');
    const g = parseInt(rgbaMatch[2]).toString(16).padStart(2, '0');
    const b = parseInt(rgbaMatch[3]).toString(16).padStart(2, '0');
    return `#${r}${g}${b}`;
  }
  return '#888888';
}

// Helper: hex to rgba
function hexToRgba(hex, alpha) {
  if (!hex) return `rgba(128, 128, 128, ${alpha})`;
  if (hex.startsWith('rgba')) return hex;
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return `rgba(128, 128, 128, ${alpha})`;
  return `rgba(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}, ${alpha})`;
}

// Helper: darken a hex color
function darkenColor(hex, percent) {
  if (!hex || !hex.startsWith('#')) return hex;
  const num = parseInt(hex.slice(1), 16);
  const r = Math.max(0, (num >> 16) - Math.round(255 * percent / 100));
  const g = Math.max(0, ((num >> 8) & 0x00ff) - Math.round(255 * percent / 100));
  const b = Math.max(0, (num & 0x0000ff) - Math.round(255 * percent / 100));
  return `#${(r << 16 | g << 8 | b).toString(16).padStart(6, '0')}`;
}
